import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { useDataProvider, useNotify } from "react-admin";
import { useNavigate, useParams } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { TIANDITU_KEY } from "../components/map/map_view.logic";
import { Waybill } from "../../models/Waybill";

type TrackingPoint = {
  readonly latitude: number;
  readonly longitude: number;
  readonly time?: string;
};

type RouteParams = {
  readonly waybillId?: string;
  readonly shipperId?: string;
};

function formatTrackingTime(value?: string): string {
  if (!value) return "-";
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) return value;
  return new Date(timestamp).toLocaleString("zh-CN", { hour12: false });
}

function extractNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function extractTrackingPoint(item: unknown): TrackingPoint | null {
  if (!item || typeof item !== "object") return null;
  const record = item as Record<string, unknown>;

  const lat = extractNumber(record.lat ?? record.latitude ?? record.Lat ?? record.Latitude);
  const lon = extractNumber(record.lng ?? record.lon ?? record.longitude ?? record.Lng ?? record.Lon ?? record.Longitude);
  if (lat !== null && lon !== null) {
    const time =
      typeof record.established === "string"
        ? record.established
        : typeof record.time === "string"
          ? record.time
          : typeof record.createdAt === "string"
            ? record.createdAt
            : typeof record.updatedAt === "string"
              ? record.updatedAt
              : undefined;
    return { latitude: lat, longitude: lon, time };
  }

  const coordinates = record.coordinates;
  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    const lonFromArray = extractNumber(coordinates[0]);
    const latFromArray = extractNumber(coordinates[1]);
    if (latFromArray !== null && lonFromArray !== null) {
      const time = typeof record.time === "string" ? record.time : undefined;
      return { latitude: latFromArray, longitude: lonFromArray, time };
    }
  }

  const location = record.location;
  if (location && typeof location === "object") {
    const locRecord = location as Record<string, unknown>;
    const locLat = extractNumber(locRecord.latitude ?? locRecord.lat ?? locRecord.Lat ?? locRecord.Latitude);
    const locLon = extractNumber(locRecord.longitude ?? locRecord.lng ?? locRecord.lon ?? locRecord.Lng ?? locRecord.Lon ?? locRecord.Longitude);
    if (locLat !== null && locLon !== null) {
      const time = typeof record.established === "string" ? record.established : typeof record.time === "string" ? record.time : undefined;
      return { latitude: locLat, longitude: locLon, time };
    }
    const locCoordinates = locRecord.coordinates;
    if (Array.isArray(locCoordinates) && locCoordinates.length >= 2) {
      const lonFromArray = extractNumber(locCoordinates[0]);
      const latFromArray = extractNumber(locCoordinates[1]);
      if (latFromArray !== null && lonFromArray !== null) {
        const time = typeof record.time === "string" ? record.time : undefined;
        return { latitude: latFromArray, longitude: lonFromArray, time };
      }
    }
  }

  return null;
}

function extractTrackingPoints(payload: unknown): TrackingPoint[] {
  if (Array.isArray(payload)) {
    return payload.map(extractTrackingPoint).filter((p): p is TrackingPoint => p !== null);
  }
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const candidates = [record.data, record.points, record.list, record.items, record.locations, record.tracks];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate.map(extractTrackingPoint).filter((p): p is TrackingPoint => p !== null);
      }
    }
    const nestedCandidates = [record.result, record.payload];
    for (const nested of nestedCandidates) {
      if (nested && typeof nested === "object") {
        const nestedRecord = nested as Record<string, unknown>;
        const nestedList = nestedRecord.data ?? nestedRecord.points ?? nestedRecord.list ?? nestedRecord.items ?? nestedRecord.locations ?? nestedRecord.tracks;
        if (Array.isArray(nestedList)) {
          return nestedList.map(extractTrackingPoint).filter((p): p is TrackingPoint => p !== null);
        }
      }
    }
  }
  return [];
}

const WaybillDrivingTrackMap: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const { waybillId, shipperId } = useParams<RouteParams>();

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const polylineRef = useRef<L.Polyline | null>(null);
  const startMarkerRef = useRef<L.CircleMarker | null>(null);
  const endMarkerRef = useRef<L.CircleMarker | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rawTrackingData, setRawTrackingData] = useState<unknown>(null);
  const [waybill, setWaybill] = useState<Waybill | null>(null);

  const trackingPoints = useMemo(() => extractTrackingPoints(rawTrackingData), [rawTrackingData]);
  const sortedTrackingPoints = useMemo(() => {
    const points = [...trackingPoints];
    points.sort((a, b) => {
      const aTime = a.time ? Date.parse(a.time) : Number.NaN;
      const bTime = b.time ? Date.parse(b.time) : Number.NaN;
      if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
      if (Number.isNaN(aTime)) return 1;
      if (Number.isNaN(bTime)) return -1;
      return aTime - bTime;
    });
    return points;
  }, [trackingPoints]);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;
    const map = L.map(mapContainerRef.current, {
      center: [35.0, 105.0],
      zoom: 5,
      minZoom: 2,
      maxZoom: 18,
      zoomControl: true,
      attributionControl: true,
    });
    const vectorLayer = L.tileLayer(
      `https://t{s}.tianditu.gov.cn/vec_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${TIANDITU_KEY}`,
      {
        subdomains: ["0", "1", "2", "3", "4", "5", "6", "7"],
        attribution: "天地图",
      }
    );
    const labelLayer = L.tileLayer(
      `https://t{s}.tianditu.gov.cn/cva_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${TIANDITU_KEY}`,
      {
        subdomains: ["0", "1", "2", "3", "4", "5", "6", "7"],
      }
    );
    vectorLayer.addTo(map);
    labelLayer.addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!waybillId || !shipperId) return;
    let isCancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const loginResult = await (dataProvider as any).fetchUserLoginToken(shipperId);
        const token = loginResult?.data as string | undefined;
        if (!token) {
          throw new Error("无法获取 /users/login token");
        }
        const trackingResult = await (dataProvider as any).fetchWaybillTracking({
          order: waybillId,
          alt: 1,
          token,
        });
        if (isCancelled) return;
        setRawTrackingData(trackingResult?.data ?? null);
      } catch (err) {
        if (isCancelled) return;
        console.error(err);
        notify("获取行车轨迹失败", { type: "error" });
        setRawTrackingData(null);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [dataProvider, notify, shipperId, waybillId]);

  useEffect(() => {
    if (!waybillId) return;
    let isCancelled = false;
    (async () => {
      try {
        const result = await dataProvider.getOne("waybills", { id: waybillId });
        if (isCancelled) return;
        setWaybill(result.data as Waybill);
      } catch (err) {
        if (isCancelled) return;
        console.error(err);
        setWaybill(null);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [dataProvider, waybillId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (polylineRef.current) {
      polylineRef.current.remove();
      polylineRef.current = null;
    }
    if (startMarkerRef.current) {
      startMarkerRef.current.remove();
      startMarkerRef.current = null;
    }
    if (endMarkerRef.current) {
      endMarkerRef.current.remove();
      endMarkerRef.current = null;
    }
    if (sortedTrackingPoints.length === 0) return;
    const latlngs = sortedTrackingPoints.map((p) => L.latLng(p.latitude, p.longitude));
    polylineRef.current = L.polyline(latlngs, { color: "#1976d2", weight: 4, opacity: 0.9 }).addTo(map);
    const startPoint = sortedTrackingPoints[0];
    const endPoint = sortedTrackingPoints[sortedTrackingPoints.length - 1];
    const start = latlngs[0];
    const end = latlngs[latlngs.length - 1];
    startMarkerRef.current = L.circleMarker(start, {
      radius: 8,
      color: "#fff",
      weight: 2,
      fillColor: "#2e7d32",
      fillOpacity: 0.9,
    }).addTo(map);
    startMarkerRef.current.bindPopup(
      `<div><strong>装车</strong><div>时间：${formatTrackingTime(startPoint.time)}</div></div>`
    );
    startMarkerRef.current.bindTooltip(
      `装车\n${formatTrackingTime(startPoint.time)}`,
      { permanent: true, direction: "top", offset: [0, -12] }
    );
    endMarkerRef.current = L.circleMarker(end, {
      radius: 8,
      color: "#fff",
      weight: 2,
      fillColor: "#d32f2f",
      fillOpacity: 0.9,
    }).addTo(map);
    endMarkerRef.current.bindPopup(
      `<div><strong>卸车</strong><div>时间：${formatTrackingTime(endPoint.time)}</div></div>`
    );
    endMarkerRef.current.bindTooltip(
      `卸车\n${formatTrackingTime(endPoint.time)}`,
      { permanent: true, direction: "top", offset: [0, -12] }
    );
    map.fitBounds(polylineRef.current.getBounds(), { padding: [40, 40] });
  }, [sortedTrackingPoints]);

  const pageTitle = useMemo(() => {
    if (!waybillId) return "行车轨迹";
    return `行车轨迹（运单：${waybillId}）`;
  }, [waybillId]);
  const startTimeText = useMemo(() => formatTrackingTime(sortedTrackingPoints[0]?.time), [sortedTrackingPoints]);
  const endTimeText = useMemo(
    () => formatTrackingTime(sortedTrackingPoints[sortedTrackingPoints.length - 1]?.time),
    [sortedTrackingPoints]
  );
  const shipperNameText = useMemo(() => waybill?.owner?.name ?? "-", [waybill]);
  const shipperPhoneText = useMemo(() => waybill?.owner?.phone ?? "-", [waybill]);
  const driverNameText = useMemo(() => waybill?.driver?.name ?? "-", [waybill]);
  const driverPhoneText = useMemo(() => waybill?.driver?.phone ?? "-", [waybill]);
  const truckPlateText = useMemo(() => waybill?.truck?.plate ?? "-", [waybill]);
  const truckTypeText = useMemo(() => waybill?.truck?.type ?? "-", [waybill]);
  const cargoNameText = useMemo(() => waybill?.cargo?.name ?? "-", [waybill]);
  const cargoImageUrl = useMemo(() => waybill?.cargo?.image ?? "", [waybill]);

  return (
    <Box sx={{ position: "relative", height: "calc(100vh - 40px)", width: "100%" }}>
      <Box ref={mapContainerRef} sx={{ position: "absolute", inset: 0 }} />
      <Paper
        elevation={6}
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          padding: 2,
          maxWidth: 520,
          zIndex: 1000,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          color: "white",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {pageTitle}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2">
              货主：{shipperNameText}
            </Typography>
            <Typography variant="body2">货主号码：{shipperPhoneText}</Typography>
            <Typography variant="body2">
              司机：{driverNameText}（{driverPhoneText}）
            </Typography>
            <Typography variant="body2">车辆：{truckPlateText}（{truckTypeText}）</Typography>
            <Typography variant="body2">货物：{cargoNameText}</Typography>
            <Typography variant="body2">轨迹点数量：{sortedTrackingPoints.length}</Typography>
            <Typography variant="body2">装车时间：{startTimeText}</Typography>
            <Typography variant="body2">卸车时间：{endTimeText}</Typography>
          </Box>
          <Box
            sx={{
              width: 120,
              height: 90,
              borderRadius: 1,
              overflow: "hidden",
              flexShrink: 0,
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {cargoImageUrl ? (
              <img
                src={cargoImageUrl}
                alt={cargoNameText}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                referrerPolicy="no-referrer"
              />
            ) : (
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.85)" }}>
                无货物图片
              </Typography>
            )}
          </Box>
        </Box>
        {isLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <CircularProgress size={18} sx={{ color: "white" }} />
            <Typography variant="body2">正在加载轨迹…</Typography>
          </Box>
        )}
      </Paper>
      <Button
        variant="contained"
        size="small"
        onClick={() => navigate("/waybills")}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          zIndex: 1000,
          backgroundColor: "rgba(25, 118, 210, 0.72)",
          "&:hover": {
            backgroundColor: "rgba(25, 118, 210, 0.88)",
          },
        }}
      >
        返回列表
      </Button>
    </Box>
  );
};

export default WaybillDrivingTrackMap;
